// Shared helpers for the day-grid bookings views (group BookingsTab + individual
// StudentBookingsGrid). Pure logic only — no JSX — so both can render their own cells.
import dayjs from "dayjs";

export const d10 = (s) => (s ? String(s).slice(0, 10) : "");
export const maxStr = (a, b) => (a > b ? a : b);
export const minStr = (a, b) => (a < b ? a : b);
export const normCampus = (v) => String(v ?? "").trim().toLowerCase();

// Product types: ordinal (as the API sends them) ↔ name, each with a colour and a stack
// priority. Residential (rank 0) stacks on its own rows; everything else (rank 1) packs
// together below.
export const TYPE_BY_ORDINAL = {
  "0": "RESIDENTIAL",
  "1": "AFTERNOON",
  "2": "WEEKEND",
  "3": "EXTERNAL",
  "4": "EXTRA",
  "5": "EVENT",
};
export const TYPE_META = {
  RESIDENTIAL: { rank: 0, label: "Residential", color: "#e3f2fd", border: "#1976d2" },
  WEEKEND: { rank: 1, label: "Weekend", color: "#e8f5e9", border: "#2e7d32" },
  AFTERNOON: { rank: 1, label: "Activity", color: "#f3e5f5", border: "#7b1fa2" },
  EXTERNAL: { rank: 1, label: "External", color: "#fff3e0", border: "#ef6c00" },
  EXTRA: { rank: 1, label: "Extra", color: "#e0f7fa", border: "#00838f" },
  EVENT: { rank: 1, label: "Event", color: "#fce4ec", border: "#c2185b" },
};
export const LEGEND_ORDER = ["RESIDENTIAL", "WEEKEND", "AFTERNOON", "EXTERNAL", "EXTRA", "EVENT"];
export const typeKey = (pt) => {
  const s = String(pt ?? "").trim().toUpperCase();
  if (TYPE_META[s]) return s;
  if (TYPE_BY_ORDINAL[s]) return TYPE_BY_ORDINAL[s];
  return "EXTERNAL";
};
export const typeMeta = (pt) => TYPE_META[typeKey(pt)];
export const typeRank = (pt) => typeMeta(pt).rank;

// Which single day a product appears in the dropdown: its start day, or — if it began
// before the range and is still running — the range's first day. null = no overlap.
export function anchorDay(p, range) {
  if (!range) return null;
  const ps = d10(p.startDate);
  const pe = d10(p.endDate);
  if (!ps || !pe) return null;
  if (pe < range.start || ps > range.end) return null;
  return ps >= range.start ? ps : range.start;
}

export function eachDay(start, end) {
  const out = [];
  if (!start || !end) return out;
  let d = dayjs(start);
  const e = dayjs(end);
  let guard = 0;
  while ((d.isSame(e) || d.isBefore(e)) && guard < 400) {
    out.push(d.format("YYYY-MM-DD"));
    d = d.add(1, "day");
    guard += 1;
  }
  return out;
}

// Greedy lane packing: each block goes in the first lane it doesn't overlap.
export function packLanes(blocks) {
  const sorted = [...blocks].sort((a, b) => a.dispStart.localeCompare(b.dispStart));
  const lanes = [];
  for (const b of sorted) {
    let placed = false;
    for (const lane of lanes) {
      if (lane[lane.length - 1].dispEnd < b.dispStart) {
        lane.push(b);
        placed = true;
        break;
      }
    }
    if (!placed) lanes.push([b]);
  }
  return lanes;
}

// Like packLanes but lets blocks that merely ABUT (one ends the day the next starts)
// share a lane (<= rather than <). Used for residential weeks, which share their
// arrival/departure day and are drawn meeting in a half/half boundary cell.
export function packLanesAdjacent(blocks) {
  const sorted = [...blocks].sort((a, b) => a.dispStart.localeCompare(b.dispStart));
  const lanes = [];
  for (const b of sorted) {
    let placed = false;
    for (const lane of lanes) {
      if (lane[lane.length - 1].dispEnd <= b.dispStart) {
        lane.push(b);
        placed = true;
        break;
      }
    }
    if (!placed) lanes.push([b]);
  }
  return lanes;
}

// Plan a non-residential lane into cell descriptors (one full-width cell per block).
export function planSimpleLaneCells(lane, days) {
  const cells = [];
  let i = 0;
  while (i < days.length) {
    const day = days[i];
    const b = lane.find((x) => x.dispStart === day);
    if (b) {
      const span = eachDay(b.dispStart, b.dispEnd).length;
      cells.push({ type: "block", block: b, span });
      i += span;
    } else {
      cells.push({ type: "empty" });
      i += 1;
    }
  }
  return cells;
}

// Plan a residential lane into half-cell descriptors: each week gets a right-half cell on
// its arrival day, a full middle body, and a left-half cell on its departure day. Where
// two weeks abut, the shared day becomes one half/half "boundary" cell.
export function planResLaneCells(lane, days) {
  const sorted = [...lane].sort((a, b) => a.dispStart.localeCompare(b.dispStart));
  const idxOf = (dd) => days.indexOf(dd);
  const cells = [];
  let cursor = 0;
  sorted.forEach((b, k) => {
    const sIdx = idxOf(b.dispStart);
    const eIdx = idxOf(b.dispEnd);
    const prev = sorted[k - 1];
    const next = sorted[k + 1];
    const startBoundary = prev && prev.dispEnd === b.dispStart;
    const endBoundary = next && next.dispStart === b.dispEnd;
    const multiDay = eIdx > sIdx;

    if (!startBoundary) {
      while (cursor < sIdx) {
        cells.push({ type: "empty" });
        cursor += 1;
      }
    }

    let bodyStart = startBoundary ? sIdx + 1 : sIdx;
    if (multiDay && !startBoundary) {
      cells.push({ type: "startHalf", block: b });
      cursor = sIdx + 1;
      bodyStart = sIdx + 1;
    }

    const bodyEnd = multiDay ? eIdx - 1 : eIdx;
    if (bodyStart <= bodyEnd) {
      cells.push({ type: "body", block: b, span: bodyEnd - bodyStart + 1 });
      cursor = bodyEnd + 1;
    } else if (!multiDay && !startBoundary) {
      cells.push({ type: "body", block: b, span: 1 });
      cursor = sIdx + 1;
    }

    if (multiDay) {
      if (endBoundary) cells.push({ type: "boundary", block: b, nextBlock: next });
      else cells.push({ type: "endHalf", block: b });
      cursor = eIdx + 1;
    }
  });
  while (cursor < days.length) {
    cells.push({ type: "empty" });
    cursor += 1;
  }
  return cells;
}
